import React, { useState, useCallback, useEffect } from "react";
import { Modal, Form, InputGroup, Button, Row, Col } from "react-bootstrap";
import StudentManagementTable from "./StudentManagementTable";
import { addStudents, updateStudent, batchDeleteStudents } from "../services/APIClient";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { parseStudentImportRows } from "../utils/studentImportParser";

function StudentManagementModal({
    show,
    onClose,
    students,
    classid,
    teacherid,
    teacherName,
    countryId,
    gradeLevel,
    name,
    onRefreshStudents,
    fromClassDetailsSpecial,
    onShowConfirmation,
    teachers,
}) {
    console.log('MODAL RENDER - Props:', {
        show,
        studentsLength: students?.length,
        classid,
        gradeLevel,
        name
    });

    // Modal state management
    const [showManagementModal, setShowManagementModal] = useState(true);
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [isProcessingChanges, setIsProcessingChanges] = useState(false);

    // Student changes state
    const [studentChanges, setStudentChanges] = useState({
        added: [],
        edited: [],
        deleted: []
    });

    // Bulk add state (following original AddStudentsFunction pattern)
    const [studentNameString, setStudentNameString] = useState("");
    const [bulkStudents, setBulkStudents] = useState([]); // Temporary array like original
    const [showVideo, setShowVideo] = useState(false);
    const [showBulkSection, setShowBulkSection] = useState(false);

    console.log('MODAL STATE:', {
        studentChangesAddedLength: studentChanges.added.length,
        bulkStudentsLength: bulkStudents.length,
        studentNameString: `"${studentNameString}"`,
        showBulkSection
    });

    // Separators for bulk input
    const commaSpace = ", ";
    const comma = ",";
    const linebreak = "\n";

    // Helper: normalize and toLower for dedupe keys
    const normalizeName = (s) =>
        (s ?? "")
            .toString()
            .replace(/<style[^>]*>.*?<\/style>/gis, "")
            .replace(/<script[^>]*>.*?<\/script>/gis, "")
            .replace(/<[^>]+>/g, "")
            .replace(/\s+/g, " ")
            .trim();

    const keyify = (s) => normalizeName(s).toLowerCase();

    // Handle changes from StudentManagementTable
    const handleStudentChangesMade = useCallback((changes) => {
        console.log('=== HANDLE STUDENT CHANGES MADE ===');
        console.log('Incoming changes:', changes);
        console.log('Previous studentChanges:', studentChanges);
        setStudentChanges(changes);
        console.log('=== END HANDLE STUDENT CHANGES MADE ===\n');
    }, []);

    // Check if there are any changes
    const hasChanges = studentChanges.added.length > 0 ||
        studentChanges.edited.length > 0 ||
        studentChanges.deleted.length > 0;

    console.log('HAS CHANGES:', hasChanges, {
        added: studentChanges.added.length,
        edited: studentChanges.edited.length,
        deleted: studentChanges.deleted.length
    });

    // FIXED: Process bulkStudents when it changes - dedupe and skip existing
    useEffect(() => {
        console.log('=== BULK STUDENTS USEEFFECT START ===');
        console.log('bulkStudents:', bulkStudents);
        console.log('bulkStudents.length:', bulkStudents.length);
        console.log('Current studentChanges.added length:', studentChanges.added.length);
        
        if (bulkStudents.length > 0) {
            // Build a set of names already present (existing class + already added)
            const existingSet = new Set([
                ...(students || []).map(s => keyify(s?.name)),
                ...studentChanges.added.map(s => keyify(s?.name))
            ]);

            // Flatten (some callers might push arrays), normalize and de-dupe
            const finalStudentArr = [];
            for (let entry of bulkStudents) {
                if (Array.isArray(entry)) {
                    finalStudentArr.push(...entry);
                } else {
                    finalStudentArr.push(entry);
                }
            }

            const localSeen = new Set(); // de-dupe within this batch
            const dedupedStudents = [];
            for (const raw of finalStudentArr) {
                const importedStudent = typeof raw === "string" ? { name: raw } : raw;
                const clean = normalizeName(importedStudent?.name);
                const key = keyify(clean);
                if (!clean) continue;
                if (localSeen.has(key)) continue;
                if (existingSet.has(key)) continue; // skip if already in class or in added
                localSeen.add(key);
                dedupedStudents.push({ ...importedStudent, name: clean });
            }

            // Create student objects with proper structure
            const studentObjects = dedupedStudents.map((student) => ({
                ...student,
                id: `temp-${Date.now()}-${Math.random()}`,
                esl: false,
                sen: false,
                asd: false,
                gdd: false,
                send: false,
                gradeLevel: gradeLevel
            }));

            console.log('Final studentObjects (deduped):', studentObjects);

            if (studentObjects.length > 0) {
                // Add to existing studentChanges.added
                setStudentChanges(prev => ({
                    ...prev,
                    added: [...prev.added, ...studentObjects]
                }));
            }

            // Clear the bulkStudents array (key part!)
            setBulkStudents([]);
        } else {
            console.log('No bulk students to process (array is empty)');
        }
        console.log('=== BULK STUDENTS USEEFFECT END ===\n');
    }, [bulkStudents, students, studentChanges.added, gradeLevel]); // include deps used in dedupe

    // Input interpreter with debugging + DE-DUPE
    function inputInterpreter() {
        console.log('=== INPUT INTERPRETER START ===');
        console.log('studentNameString:', `"${studentNameString}"`);
        console.log('Current bulkStudents before processing:', bulkStudents);
        
        if (!studentNameString.trim()) {
            console.log('Empty input, returning early');
            return;
        }

        let studentArr = [];
        
        // Keep your current behavior (Ctrl+Enter trigger outside)
        if (studentNameString.includes(linebreak)) {
            studentArr = studentNameString.split(linebreak);
        } else if (studentNameString.includes(commaSpace)) {
            // Prefer commaSpace before bare comma
            studentArr = studentNameString.split(commaSpace);
        } else if (studentNameString.includes(comma)) {
            studentArr = studentNameString.split(comma);
        } else {
            studentArr = [studentNameString];
        }

        studentArr = studentArr.map(normalizeName).filter(Boolean);

        // DE-DUPE against: current bulkStudents, already-added (session), and existing class
        setBulkStudents(prev => {
            const prevSet = new Set(prev.map(s => keyify(s?.name)));
            const existingSet = new Set([
                ...studentChanges.added.map(s => keyify(s?.name)),
                ...(students || []).map(s => keyify(s?.name))
            ]);
            const incoming = [];
            for (const n of studentArr) {
                const k = keyify(n);
                if (prevSet.has(k)) continue;
                if (existingSet.has(k)) continue;
                prevSet.add(k);
                incoming.push({ name: n });
            }
            const newValue = [...prev, ...incoming];
            console.log('New bulkStudents value (deduped):', newValue);
            return newValue;
        });
        
        // Clear input
        setStudentNameString("");
        console.log('Cleared input string');
        console.log('=== INPUT INTERPRETER END ===\n');
    }

    // Handle file upload (with DE-DUPE)
    const handleFileUpload = (event) => {
        console.log('=== FILE UPLOAD START ===');
        const file = event.target.files[0];
        if (!file) {
            console.log('No file selected');
            return;
        }

        const fileType = file.type;
        console.log('File type:', fileType);

        const finish = () => {
            // Clear the file input
            event.target.value = "";
            console.log('=== FILE UPLOAD END ===\n');
        };

        if (
            fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            fileType === "application/vnd.ms-excel" ||
            /\.xlsx$/i.test(file.name)
        ) {
            // Handle Excel files
            console.log('Processing Excel file');
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });

                    const xlsxStudents = parseStudentImportRows(sheetData);
                    console.log('Extracted XLSX students:', xlsxStudents);

                    // DE-DUPE against current bulk, added, and existing class
                    setBulkStudents(prev => {
                        const prevSet = new Set(prev.map(s => keyify(s?.name)));
                        const existingSet = new Set([
                            ...studentChanges.added.map(s => keyify(s?.name)),
                            ...(students || []).map(s => keyify(s?.name))
                        ]);
                        const incoming = [];
                        for (const importedStudent of xlsxStudents) {
                            const k = keyify(importedStudent.name);
                            if (prevSet.has(k)) continue;
                            if (existingSet.has(k)) continue;
                            prevSet.add(k);
                            incoming.push(importedStudent);
                        }
                        return [...prev, ...incoming];
                    });
                } catch (err) {
                    console.error('Failed to read XLSX:', err);
                } finally {
                    finish();
                }
            };
            reader.onerror = (e) => {
                console.error('FileReader error (XLSX):', e);
                finish();
            };
            reader.readAsArrayBuffer(file);
        } else {
            // Handle CSV files
            console.log('Processing CSV file');
            Papa.parse(file, {
                complete: (results) => {
                    try {
                        const csvStudents = parseStudentImportRows(results.data);
                        console.log('Processed CSV students:', csvStudents);

                        setBulkStudents(prev => {
                            const prevSet = new Set(prev.map(s => keyify(s?.name)));
                            const existingSet = new Set([
                                ...studentChanges.added.map(s => keyify(s?.name)),
                                ...(students || []).map(s => keyify(s?.name))
                            ]);
                            const incoming = [];
                            for (const importedStudent of csvStudents) {
                                const k = keyify(importedStudent.name);
                                if (prevSet.has(k)) continue;
                                if (existingSet.has(k)) continue;
                                prevSet.add(k);
                                incoming.push(importedStudent);
                            }
                            return [...prev, ...incoming];
                        });
                    } catch (err) {
                        console.error('Failed to parse CSV:', err);
                    } finally {
                        finish();
                    }
                },
                error: (error) => {
                    console.error('Papa.parse error:', error);
                    finish();
                }
            });
        }
    };

    // Handle Done button click
    const handleDoneClick = () => {
        console.log('Done button clicked');
        if (!hasChanges) {
            handleNoChangesConfirmation();
            return;
        }
        setShowManagementModal(false);
        setShowSummaryModal(true);
    };

    // Handle Go Back button
    const handleGoBack = () => {
        console.log('Go back button clicked');
        setShowSummaryModal(false);
        setShowManagementModal(true);
    };

    // Execute all student changes
    const executeStudentChanges = async () => {
        console.log('Executing student changes...');
        setIsProcessingChanges(true);

        try {
            // Step 1: Edit existing students
            if (studentChanges.edited.length > 0) {
                for (const editedStudent of studentChanges.edited) {
                    try {
                        const updateData = editedStudent.changes;
                        await updateStudent(editedStudent.id, updateData);
                        console.log(`Successfully updated student ${editedStudent.name}`);
                    } catch (error) {
                        console.error(`Failed to update student ${editedStudent.name}:`, error);
                    }
                }
            }

            // Step 2: Add new students
            if (studentChanges.added.length > 0) {
                const studentsForAPI = studentChanges.added.map((student) => ({
                    name: student.name,
                    ...(student.gender ? { gender: student.gender } : {}),
                    ...(student.dateOfBirth ? { dateOfBirth: student.dateOfBirth } : {}),
                    esl: student.esl || false,
                    sen: student.sen || false,
                    asd: student.asd || false,
                    gdd: student.gdd || false,
                    send: student.send || false,
                    currentGrade: student.gradeLevel,
                    countryGradeId: countryId,
                }));

                await addStudents(classid, studentsForAPI);
            }

            // Step 3: Delete students
            if (studentChanges.deleted.length > 0) {
                const studentsByReason = {};

                studentChanges.deleted.forEach(student => {
                    const reason = student.deletionReason || "No reason provided";
                    if (!studentsByReason[reason]) {
                        studentsByReason[reason] = [];
                    }
                    studentsByReason[reason].push(student);
                });

                for (const [reason, studentsGroup] of Object.entries(studentsByReason)) {
                    const validStudents = studentsGroup.filter(student => {
                        return typeof student.id === 'string' || (typeof student.id === 'number' && student.id > 0);
                    });

                    if (validStudents.length === 0) continue;

                    const deleteData = {
                        studentIds: validStudents.map(student => student.id),
                        deletionReason: reason,
                        deletedByTeacherId: teacherid,
                        deletedByTeacherName: teacherName
                    };

                    try {
                        await batchDeleteStudents(deleteData);
                    } catch (error) {
                        console.error(`Failed to delete students with reason "${reason}":`, error);
                    }
                }
            }

            // Refresh students data after all changes
            if (onRefreshStudents) {
                await onRefreshStudents();
            }

            // Show confirmation in parent and close this modal
            if (onShowConfirmation) {
                onShowConfirmation(fromClassDetailsSpecial);
            }

            setTimeout(() => {
                handleFinishSetup();
            }, 100);

        } catch (error) {
            console.error("Error executing student changes:", error);
        } finally {
            setIsProcessingChanges(false);
        }
    };

    // Handle no changes - close directly without showing confirmation
    const handleNoChangesConfirmation = () => {
        handleFinishSetup();
    };

    // Close all modals and reset state
    const handleFinishSetup = () => {
        console.log('Finishing setup - resetting all state');
        setShowManagementModal(false);
        setShowSummaryModal(false);
        setStudentChanges({
            added: [],
            edited: [],
            deleted: []
        });
        setStudentNameString("");
        setBulkStudents([]);
        setShowBulkSection(false);
        setShowVideo(false);
        onClose();
    };

    // Handle close - reset everything
    const handleClose = () => {
        console.log('Closing modal - resetting all state');
        setShowManagementModal(false);
        setShowSummaryModal(false);
        setStudentChanges({
            added: [],
            edited: [],
            deleted: []
        });
        setStudentNameString("");
        setBulkStudents([]);
        setShowBulkSection(false);
        setShowVideo(false);
        onClose();
    };

    // Reset state when modal opens
    useEffect(() => {
        console.log('=== MODAL SHOW EFFECT ===');
        console.log('show prop changed to:', show);
        
        if (show) {
            console.log('Modal opening - resetting all state');
            setShowManagementModal(true);
            setShowSummaryModal(false);
            setIsProcessingChanges(false);
            setStudentChanges({
                added: [],
                edited: [],
                deleted: []
            });
            setStudentNameString("");
            setBulkStudents([]);
            setShowBulkSection(false);
            setShowVideo(false);
        }
        console.log('=== END MODAL SHOW EFFECT ===\n');
    }, [show]);

    console.log('ABOUT TO RENDER - Final state check:', {
        show,
        bulkStudentsLength: bulkStudents.length,
        studentChangesAddedLength: studentChanges.added.length,
        bulkAddedStudentsLength: studentChanges.added.length
    });

    if (!show) return null;

    return (
        <>
            {/* Student Management Modal */}
            <Modal
                show={showManagementModal}
                onHide={handleClose}
                backdrop="static"
                keyboard={false}
                centered
                size="xl"
                dialogClassName="student-management-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Add, Edit or Remove Students</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-4">
                        <h4><strong>{name}</strong></h4>
                        <p className="mt-3">Please verify class list and update information as best known.</p>
                    </div>

                    {/* <div className="mb-4" style={{ 
                        backgroundColor: '#f8fafc', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '8px', 
                        padding: '16px' 
                    }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 style={{ margin: 0, color: '#374151' }}>
                                Bulk Add Students
                            </h5>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setShowBulkSection(!showBulkSection)}
                            >
                                {showBulkSection ? 'Hide' : 'Show'} Bulk Add
                            </Button>
                        </div>

                        {showBulkSection && (
                            <>
                                <Row className="mb-3">
                                    <Col>
                                        <Form.Label>Copy & Paste Student Names</Form.Label>
                                        <p className="text-muted small mb-2">
                                            Enter student names separated by commas, new lines, or comma-space
                                        </p>
                                        <InputGroup>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                placeholder="John Smith, Jane Doe, Mike Johnson..."
                                                value={studentNameString}
                                                onChange={(e) => setStudentNameString(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter' && e.ctrlKey) {
                                                        e.preventDefault();
                                                        inputInterpreter();
                                                    }
                                                }}
                                            />
                                            <Button 
                                                variant="outline-primary" 
                                                onClick={inputInterpreter}
                                                disabled={!studentNameString.trim()}
                                            >
                                                Add Names
                                            </Button>
                                        </InputGroup>
                                        <small className="text-muted">
                                            Tip: Press Ctrl+Enter to quickly add names
                                        </small>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col>
                                        <Form.Label>Upload Student List</Form.Label>
                                        <p className="text-muted small mb-2">
                                            Upload CSV or XLSX with student names from Aladdin / Arbor / iSAMS or any school MIS system
                                        </p>
                                        
                                        <div className="mb-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => setShowVideo(!showVideo)}
                                            >
                                                {showVideo ? 'Hide Guide' : 'Click here to watch a guide'}
                                            </Button>
                                        </div>

                                        {showVideo && (
                                            <div className="mb-3" style={{
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                overflow: 'hidden'
                                            }}>
                                                <iframe
                                                    width="60%"
                                                    height="315"
                                                    src="https://www.youtube.com/embed/v5F5Hj-q2b4?si=1e5dsf7Wpiy5hdm6"
                                                    title="YouTube video player"
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                    referrerPolicy="strict-origin-when-cross-origin"
                                                    allowFullScreen
                                                />
                                            </div>
                                        )}

                                        <Form.Control
                                            type="file"
                                            accept=".csv,.xlsx"
                                            onChange={handleFileUpload}
                                            style={{ maxWidth: '400px' }}
                                        />
                                    </Col>
                                </Row>
                            </>
                        )}
                    </div> */}

                    {/* Student Management Table */}
                    <StudentManagementTable
                        students={students}
                        classid={classid}
                        teacherid={teacherid}
                        teacherName={teacherName}
                        countryId={countryId}
                        onChangesMade={handleStudentChangesMade}
                        gradeLevel={gradeLevel}
                        bulkAddedStudents={studentChanges.added}
                        teachers={teachers || []}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="changes-indicator">
                            {hasChanges ? (
                                <span className="text-muted" style={{
                                    fontSize: '14px',
                                    color: '#6b7280',
                                    fontWeight: '500'
                                }}>
                                    {studentChanges.added.length + studentChanges.edited.length + studentChanges.deleted.length} changes pending
                                </span>
                            ) : (
                                <span className="text-muted" style={{
                                    fontSize: '14px',
                                    color: '#6b7280'
                                }}>
                                    No changes made
                                </span>
                            )}
                        </div>
                        <button
                            style={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onClick={handleDoneClick}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#2563eb';
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#3b82f6';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                            }}
                        >
                            Done
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Summary Modal - keeping the original summary modal code */}
            <Modal
                show={showSummaryModal}
                onHide={handleGoBack}
                centered
                size="lg"
                dialogClassName="summary-modal"
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Changes</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-4">
                        <h4>Please confirm the changes you want to make</h4>

                        {hasChanges ? (
                            <div className="changes-summary mt-4 text-start">
                                <p className="text-center mb-3" style={{
                                    fontSize: '16px',
                                    color: '#374151',
                                    fontWeight: '500'
                                }}>
                                    The following changes will be applied to your class:
                                </p>

                                {studentChanges.added.length > 0 && (
                                    <div style={{
                                        backgroundColor: '#f0fdf4',
                                        border: '1px solid #bbf7d0',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        marginBottom: '16px'
                                    }}>
                                        <h5 style={{
                                            color: '#059669',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            marginBottom: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span style={{ fontSize: '18px' }}>➕</span>
                                            Add {studentChanges.added.length} student{studentChanges.added.length !== 1 ? 's' : ''}
                                        </h5>
                                        <ul style={{
                                            listStyle: 'none',
                                            padding: 0,
                                            margin: 0
                                        }}>
                                            {studentChanges.added.map(student => (
                                                <li key={student.id} style={{
                                                    marginBottom: '8px',
                                                    fontSize: '14px',
                                                    color: '#374151'
                                                }}>
                                                    <strong>{student.name}</strong>
                                                    {(student.esl || student.sen || student.asd || student.gdd || student.send) && (
                                                        <span style={{
                                                            color: '#6b7280',
                                                            marginLeft: '8px',
                                                            fontSize: '13px'
                                                        }}>
                                                            with flags: {[
                                                                student.esl && 'EAL',
                                                                student.sen && 'Speech & Language',
                                                                student.asd && 'ASD',
                                                                student.gdd && 'GDD',
                                                                student.send && 'SEND'
                                                            ].filter(Boolean).join(', ')}
                                                        </span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {studentChanges.edited.length > 0 && (
                                    <div style={{
                                        backgroundColor: '#fffbeb',
                                        border: '1px solid #fed7aa',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        marginBottom: '16px'
                                    }}>
                                        <h5 style={{
                                            color: '#d97706',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            marginBottom: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span style={{ fontSize: '18px' }}>✏️</span>
                                            Update {studentChanges.edited.length} student{studentChanges.edited.length !== 1 ? 's' : ''}
                                        </h5>
                                        <ul style={{
                                            listStyle: 'none',
                                            padding: 0,
                                            margin: 0
                                        }}>
                                            {studentChanges.edited.map(student => (
                                                <li key={student.id} style={{
                                                    marginBottom: '8px',
                                                    fontSize: '14px',
                                                    color: '#374151'
                                                }}>
                                                    <strong>{student.name}</strong>
                                                    {student.fields && (
                                                        <span style={{
                                                            color: '#6b7280',
                                                            marginLeft: '8px',
                                                            fontSize: '13px'
                                                        }}>
                                                            updated: {student.fields.map(field => {
                                                                const fieldNames = {
                                                                    'name': 'name',
                                                                    'esl': 'EAL',
                                                                    'sen': 'Speech & Language',
                                                                    'asd': 'ASD',
                                                                    'gdd': 'GDD',
                                                                    'send': 'SEND'
                                                                };
                                                                return fieldNames[field] || field;
                                                            }).join(', ')}
                                                        </span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {studentChanges.deleted.length > 0 && (
                                    <div style={{
                                        backgroundColor: '#fef2f2',
                                        border: '1px solid #fecaca',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        marginBottom: '16px'
                                    }}>
                                        <h5 style={{
                                            color: '#dc2626',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            marginBottom: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span style={{ fontSize: '18px' }}>🗑️</span>
                                            Remove {studentChanges.deleted.length} student{studentChanges.deleted.length !== 1 ? 's' : ''}
                                        </h5>
                                        <ul style={{
                                            listStyle: 'none',
                                            padding: 0,
                                            margin: 0
                                        }}>
                                            {studentChanges.deleted.map(student => (
                                                <li key={student.id} style={{
                                                    marginBottom: '8px',
                                                    fontSize: '14px',
                                                    color: '#374151'
                                                }}>
                                                    <strong>{student.name}</strong>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '48px 24px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '8px',
                                border: '2px dashed #d1d5db'
                            }}>
                                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                                <p style={{
                                    fontSize: '18px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '8px'
                                }}>
                                    No changes were made to your class
                                </p>
                                <p style={{ color: '#6b7280' }}>
                                    Your class is ready to go!
                                </p>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-between w-100">
                        <button
                            style={{
                                backgroundColor: '#6b7280',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                opacity: isProcessingChanges ? '0.6' : '1'
                            }}
                            onClick={handleGoBack}
                            disabled={isProcessingChanges}
                        >
                            Go Back
                        </button>
                        <button
                            style={{
                                backgroundColor: hasChanges ? '#10b981' : '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                opacity: isProcessingChanges ? '0.6' : '1',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            onClick={hasChanges ? executeStudentChanges : handleNoChangesConfirmation}
                            disabled={isProcessingChanges}
                        >
                            {isProcessingChanges ? (
                                <>
                                    <span style={{
                                        display: 'inline-block',
                                        width: '12px',
                                        height: '12px',
                                        border: '2px solid transparent',
                                        borderTop: '2px solid white',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}></span>
                                    Processing...
                                </>
                            ) : hasChanges ? (
                                'Confirm Changes'
                            ) : (
                                "Let's Go!"
                            )}
                        </button>
                    </div>
                    <style>
                        {`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}
                    </style>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default StudentManagementModal;
